// The desk page's suggestion cards: questions worth asking now, each written from the market as it is
// (the Market Flow board's reading of the session, the breakout scan, India VIX, the user's paper
// trades), with the figure that makes it worth asking and the time that figure is from. Computed
// here from MoneyHQ's data; nothing is invented when a source is missing (its card is left out).
import { useEffect, useMemo, useState } from 'react';
import type { Session } from '@planb/core';
import { api } from '@/lib/api';
import type { Mode } from '@/lib/client/use-market';
import { marketIndex, marketReading } from './market';

export interface Suggestion {
  id: string;
  kind: 'market' | 'mover' | 'faller' | 'sector' | 'setups' | 'paper' | 'start';
  title: string;
  detail: string;
  prompt: string;
  /** When the figure is from ("live 10:42", "19 Oct close"). */
  asOf: string;
}

interface Mood { date: string; indiaVix?: number; indiaVixChangePct?: number }
interface Scan { rows: { symbol: string; group: string; measuredEdge: boolean; sessionsSinceEntry: number }[]; counts: { newSignalsToday: number; openTrends: number } }
interface Paper { trade: { symbol: string }; state: { status: string; final: boolean; r: number } }

const signed = (v: number, d = 2) => `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`;
const day = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const capitalText = () => {
  try {
    const c = Number((localStorage.getItem('mhq.capital') ?? '').replace(/[^\d.]/g, ''));
    return c > 0 ? `₹${c.toLocaleString('en-IN')}` : '₹1 lakh';
  } catch { return '₹1 lakh'; }
};

// The last of each, kept across visits: the cards show at once and refresh behind.
const kept: { mood?: Mood | null; scan?: Scan | null; paper?: Paper[] | null } = {};

/** Up to `max` cards for the market now (null while loading). */
export function useSuggestions(session: Session | null, mode: Mode, max = 4): Suggestion[] | null {
  const [mood, setMood] = useState<Mood | null>(kept.mood ?? null);
  const [scan, setScan] = useState<Scan | null>(kept.scan ?? null);
  const [paper, setPaper] = useState<Paper[] | null>(kept.paper ?? null);
  const [loaded, setLoaded] = useState(() => ('mood' in kept ? 3 : 0));
  useEffect(() => {
    let stop = false;
    const get = <T,>(path: string, set: (v: T | null) => void) => api(path).then((r) => (r.ok ? r.json() as Promise<T> : null)).catch(() => null)
      .then((v) => { if (!stop) { set(v); setLoaded((n) => n + 1); } });
    void get<Mood>('/v1/market/mood', (v) => { kept.mood = v; setMood(v); });
    void get<Scan>('/v1/setups/breakouts', (v) => { kept.scan = v; setScan(v); });
    void get<Paper[]>('/v1/paper', (v) => { kept.paper = v; setPaper(v); });
    return () => { stop = true; };
  }, []);

  return useMemo(() => {
    if (loaded < 3 && !session) return null;
    const out: Suggestion[] = [];
    const reading = session ? marketReading(session, marketIndex('NIFTY500')) : null;
    const board = reading && !('error' in reading) ? reading : null;
    const when = board ? (mode === 'live' ? `live ${board.asOf}` : `${day(board.session)} close`) : '';
    if (board) {
      const vix = mood?.indiaVix ? ` · VIX ${mood.indiaVix.toFixed(2)}${mood.indiaVixChangePct ? ` (${signed(mood.indiaVixChangePct, 1)}, ${day(mood.date)})` : ''}` : '';
      out.push({ id: 'market', kind: 'market', title: 'How is the market today?', detail: `NIFTY 500: ${board.breadth.up} up, ${board.breadth.down} down${vix}`, prompt: 'How is the market today?', asOf: when });
    }
    if (scan && scan.rows.length) {
      const top = scan.rows.find((r) => r.measuredEdge);
      const fresh = scan.counts.newSignalsToday;
      out.push({
        id: 'setups', kind: 'setups', title: `I have ${capitalText()}. What is the right stock?`,
        detail: `${fresh ? `${fresh} new breakout signal${fresh > 1 ? 's' : ''} · ` : ''}${scan.counts.openTrends} stocks in a breakout trend${top ? ` · top: ${top.symbol} (group ${top.group})` : ''}`,
        prompt: `I have ${capitalText()}. What is the right stock?`, asOf: 'last close',
      });
    }
    if (board) {
      const up = board.strongest.find((x) => x.changePct > 0);
      if (up) out.push({ id: `mover-${up.symbol}`, kind: 'mover', title: `Why is ${up.symbol} moving?`, detail: `${signed(up.changePct)} on ${up.volumeVsUsual} its usual volume · ${up.sector}`, prompt: `Why is ${up.symbol} moving today?`, asOf: when });
    }
    const open = (paper ?? []).filter((p) => !p.state.final && p.state.status === 'open');
    if (open.length) {
      const p = open[0];
      out.push({ id: `paper-${p.trade.symbol}`, kind: 'paper', title: `Review my ${p.trade.symbol} paper trade`, detail: `Open at ${p.state.r >= 0 ? '+' : ''}${p.state.r.toFixed(2)}R${open.length > 1 ? ` · ${open.length - 1} more open` : ''}`, prompt: `Review my open ${p.trade.symbol} paper trade`, asOf: 'now' });
    }
    if (board) {
      const s = board.sectorsStrongestFirst[0];
      if (s) out.push({ id: `sector-${s.sector}`, kind: 'sector', title: `What is driving ${s.sector}?`, detail: `Leads the NIFTY 500: ${signed(s.avgMovePct)} on average, ${s.buyersInControl} with buyers in control`, prompt: `Which ${s.sector} stocks are leading today, and is any of them in a tested setup?`, asOf: when });
      const down = board.weakest.find((x) => x.changePct < 0);
      if (down) out.push({ id: `faller-${down.symbol}`, kind: 'faller', title: `Why is ${down.symbol} falling?`, detail: `${signed(down.changePct)} on ${down.volumeVsUsual} its usual volume · ${down.sector}`, prompt: `Why is ${down.symbol} falling today?`, asOf: when });
    }
    if (paper && paper.length === 0) out.unshift({ id: 'start', kind: 'start', title: 'Guide me through my first paper trade', detail: 'Five steps on real market data: find, plan, size, place, review', prompt: 'Guide me through my first paper trade, step by step', asOf: '' });
    return out.slice(0, max);
  }, [session, mode, mood, scan, paper, loaded, max]);
}
